import React, { useEffect, useRef, useState } from 'react';
import { createChart, CrosshairMode, LineStyle } from 'lightweight-charts';
import PageServices from '../../services/PageServices';
import { useTheme } from '../../context/ThemeContext';

// Greeks Graph (AOC style): per minute of the session, the total Delta / Gamma / Theta / Vega
// (and average IV) of all Calls and all Puts, plus CE − PE and a zero line
// (GET /history/greeksGraph). Loads when the surrounding popup opens, refreshes every minute.

const TABS = [
  { id: 'delta', label: 'Delta' },
  { id: 'gamma', label: 'Gamma', scale: 1000, note: '×1000' },
  { id: 'theta', label: 'Theta' },
  { id: 'vega', label: 'Vega' },
  { id: 'iv', label: 'IV', note: '%' },
];
const REFRESH_MS = 60 * 1000;
const IST_OFFSET_SEC = 330 * 60; // lightweight-charts shows UTC; shift so the axis reads IST
const COLORS = { ce: '#2e9d4f', pe: '#d63c3c', diff: '#2f5be0', zero: '#8a8f98' };

const GreeksGraph = ({ symbol, date }) => {
  const rootRef = useRef(null);
  const boxRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef({});
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('delta');
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('idle');
  const { theme } = useTheme();
  const dark = theme === 'dark';

  // fetch / refresh only while the popup is open
  useEffect(() => {
    const modal = rootRef.current?.closest('.modal');
    if (!modal) return undefined;
    const onShow = () => setOpen(true);
    const onHide = () => setOpen(false);
    modal.addEventListener('shown.bs.modal', onShow);
    modal.addEventListener('hidden.bs.modal', onHide);
    return () => {
      modal.removeEventListener('shown.bs.modal', onShow);
      modal.removeEventListener('hidden.bs.modal', onHide);
    };
  }, []);

  useEffect(() => {
    if (!open || !symbol) return undefined;
    let cancelled = false;
    const load = (first) => {
      if (first) setStatus('loading');
      PageServices.getGreeksGraph(symbol, date)
        .then((res) => {
          if (cancelled) return;
          setData(res?.data || null);
          setStatus(res?.data?.points?.length ? 'ready' : 'empty');
        })
        .catch(() => { if (!cancelled && first) setStatus('empty'); });
    };
    load(true);
    const id = date ? null : setInterval(() => load(false), REFRESH_MS);
    return () => { cancelled = true; if (id) clearInterval(id); };
  }, [open, symbol, date]);

  // chart (re)created for the theme
  useEffect(() => {
    if (!open || !boxRef.current) return undefined;
    const text = dark ? '#cbd5e1' : '#334155';
    const grid = dark ? '#1f2937' : '#eef1f5';
    const chart = createChart(boxRef.current, {
      autoSize: true,
      layout: { background: { color: dark ? '#0f172a' : '#ffffff' }, textColor: text, fontSize: 12 },
      grid: { vertLines: { color: grid }, horzLines: { color: grid } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: grid },
      timeScale: { borderColor: grid, timeVisible: true, secondsVisible: false },
      localization: { priceFormatter: (v) => v.toFixed(2) },
    });
    const line = (color, title, extra = {}) => chart.addLineSeries({
      color, lineWidth: 2, title, priceLineVisible: true, lastValueVisible: true, ...extra,
    });
    seriesRef.current = {
      ce: line(COLORS.ce, 'CE'),
      pe: line(COLORS.pe, 'PE'),
      diff: line(COLORS.diff, 'CE−PE'),
      zero: line(COLORS.zero, 'Zero', { lineWidth: 1, lineStyle: LineStyle.Dashed, priceLineVisible: false }),
    };
    chartRef.current = chart;
    return () => { chart.remove(); chartRef.current = null; seriesRef.current = {}; };
  }, [open, dark]);

  // data / tab → series
  useEffect(() => {
    const s = seriesRef.current;
    if (!s.ce || !data?.points?.length) return;
    const cfg = TABS.find((t) => t.id === tab);
    const k = cfg.scale || 1;
    const pts = data.points;
    const map = (fn) => pts.map((p) => ({ time: p.time + IST_OFFSET_SEC, value: Math.round(fn(p) * k * 100) / 100 }));
    s.ce.setData(map((p) => p.ce[tab]));
    s.pe.setData(map((p) => p.pe[tab]));
    s.diff.setData(map((p) => p.ce[tab] - p.pe[tab]));
    s.zero.setData(map(() => 0));
    const label = `${cfg.label}`;
    s.ce.applyOptions({ title: `${label} CE` });
    s.pe.applyOptions({ title: `${label} PE` });
    s.diff.applyOptions({ title: `${label} (CE-PE)` });
    chartRef.current?.timeScale().fitContent();
  }, [data, tab, dark]);

  const cfg = TABS.find((t) => t.id === tab);
  return (
    <div ref={rootRef} className="greeks-graph">
      <div className="greeks-graph-bar">
        <div className="greeks-graph-tabs" role="tablist">
          {TABS.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
              className={tab === t.id ? 'active' : ''} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="greeks-graph-legend">
          <span style={{ color: COLORS.ce }}>— {cfg.label} CE</span>
          <span style={{ color: COLORS.pe }}>— {cfg.label} PE</span>
          <span style={{ color: COLORS.diff }}>— {cfg.label} (CE-PE)</span>
          <span style={{ color: COLORS.zero }}>-- Zero line</span>
          {cfg.note && <small>({cfg.note})</small>}
        </div>
      </div>
      <div className="greeks-graph-box">
        <div ref={boxRef} className="greeks-graph-canvas" />
        {status !== 'ready' && (
          <p className="greeks-graph-msg">{status === 'loading' ? 'Loading…' : 'No data available'}</p>
        )}
      </div>
      {data?.date && <small className="greeks-graph-foot">{data.symbol} · {data.date} · expiry {data.expiry} · totals of all strikes</small>}
    </div>
  );
};

export default GreeksGraph;
